import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { groupedTools } from "../data/toolsData";
import { blogContent } from "../blogs/BlogDetails";
import { SITE_URL, SITE_HOME, pageUrl } from "../utils/paths";
const SUPPORTED_LANGS = ["en", "es", "zh-CN", "de", "fr", "ja"];

const OG_LOCALE = {
  en: "en_US",
  es: "es_ES",
  "zh-CN": "zh_CN",
  de: "de_DE",
  fr: "fr_FR",
  ja: "ja_JP",
};

const HTML_LANG = {
  en: "en",
  es: "es",
  "zh-CN": "zh-CN",
  de: "de",
  fr: "fr",
  ja: "ja",
};

function parsePath(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const lang = SUPPORTED_LANGS.includes(segments[0]) ? segments[0] : "en";
  const rest = SUPPORTED_LANGS.includes(segments[0])
    ? "/" + segments.slice(1).join("/")
    : pathname;
  const normalizedRest = rest.replace(/\/$/, "") || "";
  return { lang, rest: normalizedRest };
}

function findTool(rest) {
  const all = Object.values(groupedTools).flat();
  return all.find((t) => t.path === rest);
}

// Extract the first <p>...</p> from a blog post's HTML content and return a
// plain-text, single-line snippet truncated at a word boundary. Mirrors the
// description-extraction logic in scripts/prerender-meta.js so the runtime
// meta description matches the prerendered one search engines saw first.
function extractBlogDescription(content, maxLen = 160) {
  if (!content) return "";
  const pMatch = content.match(/<p>([\s\S]*?)<\/p>/i);
  const raw = pMatch ? pMatch[1] : "";
  const stripped = raw.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  if (stripped.length <= maxLen) return stripped;
  const slice = stripped.slice(0, maxLen + 1);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > 0 ? slice.slice(0, lastSpace) : slice.slice(0, maxLen);
  return `${cut.replace(/[.,;:!?]+$/, "")}…`;
}

function findBlogPost(rest) {
  const m = rest.match(/^\/blogs\/([a-z0-9-]+)$/);
  if (!m) return null;
  const post = blogContent[m[1]];
  return post ? { slug: m[1], ...post } : null;
}

const PageSEO = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { lang, rest } = parsePath(pathname);
  const tool = findTool(rest);
  const blogPost = findBlogPost(rest);

  const siteName = t("seo_site_name");
  const isHome = rest === "" || rest === "/";
  const isBlogsList = rest === "/blogs";
  const isHelp = rest === "/help";
  const isTerms = rest === "/terms";
  const isPrivacy = rest === "/privacy";

  const baseTitle = isHome
    ? t("seo_home_title")
    : isBlogsList
      ? t("seo_blogs_title")
      : isHelp
        ? t("seo_help_title")
        : isTerms
          ? t("seo_terms_title")
          : isPrivacy
            ? t("seo_privacy_title")
            : blogPost
              ? `${blogPost.title} | ${siteName}`
              : tool?.nameKey
                ? `${t(tool.nameKey)} | ${siteName}`
                : t("seo_default_title");

  const description = isHome
    ? t("seo_home_description")
    : isBlogsList
      ? t("seo_blogs_description")
      : isHelp
        ? t("seo_help_description")
        : isTerms
          ? t("seo_terms_description")
          : isPrivacy
            ? t("seo_privacy_description")
            : blogPost
              ? extractBlogDescription(blogPost.content)
              : tool?.descriptionKey
                ? t(tool.descriptionKey)
                : t("seo_default_description");

  // Use the same homepage-derived brand image (1200x630 PNG composited from
  // /icon.png by scripts/generate-og-image.js) for every route, so social
  // previews stay aligned with the icon users see in the homepage header.
  const image = `${SITE_URL}/og-image.png`;

  // Trailing-slash URLs are the final 200 form on S3/CloudFront; must match
  // scripts/prerender-meta.js byte-for-byte.
  const canonical = pageUrl(lang, rest);
  const xDefault = pageUrl("en", rest);
  const homeUrl = pageUrl(lang);

  const ogLocale = OG_LOCALE[lang] || OG_LOCALE.en;
  const htmlLang = HTML_LANG[lang] || "en";

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: SITE_HOME,
    inLanguage: htmlLang,
  };

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteName,
    url: SITE_HOME,
    logo: `${SITE_URL}/android-chrome-512x512.png`,
  };

  const softwareJsonLd = tool
    ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: t(tool.nameKey),
        description,
        url: canonical,
        applicationCategory: "DeveloperApplication",
        operatingSystem: "Any (Web Browser)",
        inLanguage: htmlLang,
        image,
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
        publisher: {
          "@type": "Organization",
          name: siteName,
          url: SITE_HOME,
        },
      }
    : null;

  const articleJsonLd = blogPost
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: blogPost.title,
        description,
        url: canonical,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        image,
        inLanguage: htmlLang,
        author: { "@type": "Organization", name: siteName, url: SITE_HOME },
        publisher: {
          "@type": "Organization",
          name: siteName,
          url: SITE_HOME,
          logo: {
            "@type": "ImageObject",
            url: `${SITE_URL}/android-chrome-512x512.png`,
            width: 512,
            height: 512,
          },
        },
      }
    : null;

  const breadcrumbJsonLd = tool
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: siteName,
            item: homeUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: t(tool.nameKey),
            item: canonical,
          },
        ],
      }
    : isBlogsList
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: siteName,
              item: homeUrl,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: t("blogs_title"),
              item: canonical,
            },
          ],
        }
      : blogPost
        ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: siteName,
                item: homeUrl,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("blogs_title"),
                item: pageUrl(lang, "/blogs"),
              },
              {
                "@type": "ListItem",
                position: 3,
                name: blogPost.title,
                item: canonical,
              },
            ],
          }
        : isHelp
          ? {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: siteName,
                  item: homeUrl,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: baseTitle,
                  item: canonical,
                },
              ],
            }
          : isTerms || isPrivacy
            ? {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: siteName,
                    item: homeUrl,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: t(
                      isTerms ? "terms_conditions_title" : "privacy_policy_title",
                    ),
                    item: canonical,
                  },
                ],
              }
            : null;

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{baseTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {SUPPORTED_LANGS.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={HTML_LANG[l]}
          href={pageUrl(l, rest)}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={xDefault} />

      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={baseTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={tool || blogPost ? "article" : "website"} />
      <meta property="og:locale" content={ogLocale} />
      {SUPPORTED_LANGS.filter((l) => l !== lang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={OG_LOCALE[l]} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={baseTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      {softwareJsonLd && (
        <script type="application/ld+json">{JSON.stringify(softwareJsonLd)}</script>
      )}
      {articleJsonLd && (
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      )}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      )}
    </Helmet>
  );
};

export default PageSEO;
